import axios from 'axios';
import { API_BASE_URL } from '../config';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
    }
);

// API functions

// Settings
export const settingsAPI = {
    getAll: () => api.get('/settings'),
    update: (key, value) => api.put(`/settings/${key}`, { value }),
    bulkUpdate: (settings) => api.put('/settings', { settings })
};

// Members
export const membersAPI = {
    getAll: () => api.get('/members'),
    getActive: () => api.get('/members/active'),
    getById: (id) => api.get(`/members/${id}`),
    getStats: () => api.get('/members/stats'),
    create: (data) => api.post('/members', data),
    update: (id, data) => api.put(`/members/${id}`, data),
    delete: (id) => api.delete(`/members/${id}`)
};

// Customers
export const customersAPI = {
    getAll: () => api.get('/customers'),
    getActive: () => api.get('/customers/active'),
    getById: (id) => api.get(`/customers/${id}`),
    search: (q) => api.get(`/customers/search?q=${q}`),
    create: (data) => api.post('/customers', data),
    update: (id, data) => api.put(`/customers/${id}`, data),
    delete: (id) => api.delete(`/customers/${id}`)
};

// Expense Types
export const expenseTypesAPI = {
    getAll: () => api.get('/expense-types'),
    getActive: () => api.get('/expense-types/active'),
    create: (data) => api.post('/expense-types', data),
    update: (id, data) => api.put(`/expense-types/${id}`, data),
    delete: (id) => api.delete(`/expense-types/${id}`)
};

// Categories
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    getActive: () => api.get('/categories/active'),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`)
};

// Transactions
export const transactionsAPI = {
    getAll: (params = {}) => api.get('/transactions', { params }),
    getById: (id) => api.get(`/transactions/${id}`),
    create: (data) => api.post('/transactions', data),
    update: (id, data) => api.put(`/transactions/${id}`, data),
    delete: (id) => api.delete(`/transactions/${id}`),
    previewShares: (data) => api.post('/transactions/preview-shares', data)
};

// Invoices
export const invoicesAPI = {
    getAll: (params = {}) => api.get('/invoices', { params }),
    getById: (id) => api.get(`/invoices/${id}`),
    create: (data) => api.post('/invoices', data),
    update: (id, data) => api.put(`/invoices/${id}`, data),
    delete: (id) => api.delete(`/invoices/${id}`)
};

// Purchase Orders
export const purchaseOrdersAPI = {
    getAll: (params = {}) => api.get('/purchase-orders', { params }),
    getById: (id) => api.get(`/purchase-orders/${id}`),
    create: (data) => api.post('/purchase-orders', data),
    update: (id, data) => api.put(`/purchase-orders/${id}`, data),
    delete: (id) => api.delete(`/purchase-orders/${id}`),
    convertToInvoice: (id, data) => api.post(`/purchase-orders/${id}/convert-to-invoice`, data)
};

// Payments
export const paymentsAPI = {
    getAll: (params = {}) => api.get('/payments', { params }),
    getById: (id) => api.get(`/payments/${id}`),
    create: (data) => api.post('/payments', data),
    delete: (id) => api.delete(`/payments/${id}`)
};

// Payment Modes
export const paymentModesAPI = {
    getAll: () => api.get('/payment-modes'),
    getActive: () => api.get('/payment-modes/active'),
    create: (data) => api.post('/payment-modes', data),
    update: (id, data) => api.put(`/payment-modes/${id}`, data),
    delete: (id) => api.delete(`/payment-modes/${id}`)
};

// Reports
export const reportsAPI = {
    getSOA: (params = {}) => api.get('/reports/soa', { params }),
    getIncomeStatement: (params = {}) => api.get('/reports/income', { params }),
    getExpenseStatement: (params = {}) => api.get('/reports/expense', { params }),
    getBalanceSheet: (params = {}) => api.get('/reports/balance-sheet', { params }),
    getMemberBreakdown: (params) => api.get('/reports/member-breakdown', { params })
};

// Dashboard
export const dashboardAPI = {
    getStats: (params = {}) => api.get('/dashboard/stats', { params }),
    getMemberDistribution: () => api.get('/dashboard/member-distribution')
};

export default api;
